import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Card } from 'flowbite-react';
import { toast } from 'react-toastify';
import {
  HiCheck,
  HiX,
  HiArrowRight,
  HiOutlineGlobe,
  HiOutlineDeviceMobile,
  HiOutlineDeviceTablet,
  HiOutlineChip,
  HiOutlineCreditCard,
  HiOutlineLocationMarker,
  HiOutlineWifi,
  HiOutlineUser,
  HiOutlineUserGroup,
  HiCurrencyDollar,
  HiGift,
  HiInformationCircle,
  HiOutlineHome
} from 'react-icons/hi';
import { MdFlight } from 'react-icons/md';
import HomeAwayRevealSection from '../components/home/HomeAwayRevealSection';
const RightDrawer = React.lazy(() => import('../components/shared/RightDrawer'));
const DataCalculator = React.lazy(() => import('../components/shared/DataCalculator'));
const BonusInfoDrawer = React.lazy(() => import('../components/shared/BonusInfoDrawer'));
import { Helmet } from '@dr.pogodin/react-helmet';
import DynamicMetaTags from '../components/shared/DynamicMetaTags';
import PendingOAuthModal from '../components/auth/PendingOAuthModal';
import { usePublicSettings } from '../contexts/PublicSettingsContext';
import { useAuth } from '../contexts/AuthContext';
import { getFlagEmoji } from '../utils/countryFlags';
import { getApiUrl } from '../utils/apiConfig';
import { purchaseService, purchaseHelpers } from '../services/purchaseService';
import { referralHelpers } from '../services/referralService';
import { useTopCountries } from '../hooks/useInference';
import axios from 'axios';
// anime.js removed — replaced with CSS keyframe animations for better performance

const HomePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { countryCount } = usePublicSettings();
  const { authenticateWithGoogle, authenticateWithApple, isAuthenticated } = useAuth();

  // Get recommended countries from inference engine
  const { countries: inferredCountries } = useTopCountries(5);
  const [showPremiumDrawer, setShowPremiumDrawer] = useState(false);
  const [showSoloDrawer, setShowSoloDrawer] = useState(false);
  const [showPayGoDrawer, setShowPayGoDrawer] = useState(false);
  const [showPremiumBonusDrawer, setShowPremiumBonusDrawer] = useState(false);
  const [showSoloBonusDrawer, setShowSoloBonusDrawer] = useState(false);
  const [showPayGoBonusDrawer, setShowPayGoBonusDrawer] = useState(false);
  const [showPendingOAuthModal, setShowPendingOAuthModal] = useState(false);
  const [currentFaqIndex, setCurrentFaqIndex] = useState(0);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [isFaqHovered, setIsFaqHovered] = useState(false);
  const [showReactionPicker, setShowReactionPicker] = useState(null);
  const [faqReactions, setFaqReactions] = useState({});
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // State for tracking home country selections from drawers
  const [homeCountryPrice, setHomeCountryPrice] = useState(0.99); // Default US price
  const [homeCountryName, setHomeCountryName] = useState('the US');
  const [fiveBucksImage] = useState(() => Math.floor(Math.random() * 5) + 1);

  // Country pricing state
  const [countryPricingData, setCountryPricingData] = useState({});
  const [selectedTier, setSelectedTier] = useState(1);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [visibleGroupStart, setVisibleGroupStart] = useState(0); // For "View More" functionality

  // Hero video ref and reduced-motion preference
  const heroVideoRef = useRef(null);
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  const shouldAutoplay = useRef(
    (() => {
      if (typeof navigator !== 'undefined' && navigator.connection) {
        const conn = navigator.connection;
        if (conn.saveData || conn.effectiveType === '2g' || conn.effectiveType === 'slow-2g') {
          return false;
        }
      }
      return true;
    })()
  );

  // Ref for lazy-loading pricing data when section is near viewport
  const pricingSectionRef = useRef(null);
  const fiveBucksRef = useRef(null);
  const pricingFetchedRef = useRef(false);

  // State for rotating hero messages
  const [currentHeroMessage, setCurrentHeroMessage] = useState(0);
  const heroMessages = [
    { title: "0 Planning", text: "Don't know if you need 3GB or 5GB? Just top up and pay only for what you use." },
    { title: "0 Switching", text: "Landing in Japan after France? No extra setup needed, Jetogo connects you right away." },
    { title: "0 Management", text: "Credit never expires. Your balance waits for your next trip." }
  ];

  // Handle hash navigation after pricing data loads
  useEffect(() => {
    if (location.hash === '#comparison' && !pricingLoading) {
      // Small delay to ensure DOM has updated after pricingLoading changed
      const timeoutId = setTimeout(() => {
        const element = document.getElementById('comparison');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // Remove hash from URL after scroll
          window.history.replaceState(null, '', window.location.pathname);
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [location.hash, pricingLoading]);


  // Video ref for viewport detection
  const videoRef = useRef(null);
  const [videoSrc, setVideoSrc] = useState('/assets/images/esim-ipad-990.mp4');
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
  const [useGif, setUseGif] = useState(false);

  // Refs for animated text lines
  const line1Ref = useRef(null);
  const line2Ref = useRef(null);
  const line3Ref = useRef(null);

  // (debug log removed for production performance)

  // Check for pending OAuth completion on page load (deferred — not critical for initial render)
  useEffect(() => {
    const checkPendingOAuth = async () => {
      if (isAuthenticated()) return;

      const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
      };

      const userId = getCookie('jetogo_user_uuid');
      if (!userId) return;

      try {
        const response = await axios.get(`${getApiUrl()}/users/${userId}/pending-oauth`);
        if (response.data.hasPendingOAuth && response.data.hasActiveSubscription) {
          setShowPendingOAuthModal(true);
        }
      } catch (error) {
        // Silently fail - don't interrupt user experience
      }
    };

    // Defer this non-critical API call so it doesn't compete with LCP resources
    const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 2000));
    schedule(() => checkPendingOAuth());
  }, [isAuthenticated]);

  // Check for referral data and show toast notification
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const fromReferral = searchParams.get('from_referral');

    if (fromReferral === 'true') {
      const referralData = referralHelpers.getReferralForSignup();

      if (referralData && referralData.code) {
        // Get referrer name from sessionStorage (set during validation)
        const referrerName = sessionStorage.getItem('referrer_name') || 'A friend';
        const bonusAmount = '$10';

        // Show toast notification
        toast.success(
          `🎉 Referred by ${referrerName}. ${bonusAmount} Bonus applied!`,
          {
            position: 'top-center',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );

        // Remove the query parameter to prevent showing toast again on refresh
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [location]);

  // Check video format support
  useEffect(() => {
    const video = document.createElement('video');
    const canPlayMP4 = video.canPlayType('video/mp4');
    setUseGif(canPlayMP4 === '');
  }, []);

  // Viewport detection for video playback
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const handleVideoEnded = () => {
      if (hasPlayedOnce) return;
      setHasPlayedOnce(true);
      // Switch to loop video
      if (useGif) {
        setVideoSrc('/assets/images/ipadLoop.gif');
      } else {
        setVideoSrc('/assets/images/ipadLoop_960.mp4');
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 1.0) {
            videoElement.play();
          } else {
            videoElement.pause();
            // Reset to original video when leaving viewport
            if (hasPlayedOnce) {
              setVideoSrc('/assets/images/esim-ipad-990.mp4');
              setHasPlayedOnce(false);
            }
          }
        });
      },
      { threshold: 1.0 }
    );

    videoElement.addEventListener('ended', handleVideoEnded);
    observer.observe(videoElement);

    return () => {
      videoElement.removeEventListener('ended', handleVideoEnded);
      observer.disconnect();
    };
  }, [hasPlayedOnce, useGif]);


  // Lazy-load country pricing data when pricing section approaches viewport
  useEffect(() => {
    const initializeCountryPricing = async () => {
      if (pricingFetchedRef.current) return;
      pricingFetchedRef.current = true;

      try {
        setPricingLoading(true);

        // Detect user's country via Cloudflare
        const detectedCountry = await purchaseService.detectHomeCountry();

        // Fetch all country pricing data
        const response = await axios.get(getApiUrl('/gbprice'));

        if (response.data.status === 'success') {
          const countries = response.data.data || [];

          // Find pricing for detected country
          const countryData = countries.find(c => c.country_code === detectedCountry);

          if (countryData) {
            setHomeCountryPrice(parseFloat(countryData.price_per_gb));
            setHomeCountryName(countryData.country);
          } else {
            const usData = countries.find(c => c.country_code === 'US');
            setHomeCountryPrice(usData ? parseFloat(usData.price_per_gb) : 0.99);
          }

          // Group countries by tier for the pricing table
          const tierData = {};
          countries.forEach(country => {
            const tier = country.tier;
            if (!tierData[tier]) {
              tierData[tier] = {
                countries: [],
                minPrice: Number(country.price_per_gb),
                maxPrice: Number(country.price_per_gb)
              };
            }
            tierData[tier].countries.push(country);
            tierData[tier].minPrice = Math.min(tierData[tier].minPrice, Number(country.price_per_gb));
            tierData[tier].maxPrice = Math.max(tierData[tier].maxPrice, Number(country.price_per_gb));
          });

          Object.keys(tierData).forEach(tier => {
            tierData[tier].countries.sort((a, b) => a.country.localeCompare(b.country));
          });

          setCountryPricingData(tierData);

          const sortedTiers = Object.keys(tierData).sort((a, b) => Number(a) - Number(b));
          if (sortedTiers.length > 0) {
            setSelectedTier(Number(sortedTiers[0]));
          }
        }
      } catch (error) {
        console.error('Error initializing country pricing:', error);
        setHomeCountryPrice(0.99);
      } finally {
        setPricingLoading(false);
      }
    };

    // If URL has #comparison hash, load immediately (user navigated directly to pricing)
    if (location.hash === '#comparison') {
      initializeCountryPricing();
      return;
    }

    // Otherwise, use IntersectionObserver to defer loading until user scrolls near pricing
    const el = pricingSectionRef.current;
    if (!el) {
      // Fallback: load after idle
      const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 3000));
      schedule(() => initializeCountryPricing());
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          initializeCountryPricing();
        }
      },
      { rootMargin: '400px' } // Start loading 400px before section enters viewport
    );
    observer.observe(el);

    return () => observer.disconnect();
  }, [location.hash]);

  // Animate text lines on mount using CSS animations (no JS blocking)
  useEffect(() => {
    const animateTextLine = (lineRef, baseDelayMs = 0) => {
      if (!lineRef.current) return;
      const lettersSpan = lineRef.current.querySelector('.letters');
      if (!lettersSpan) return;

      // Wrap each non-space character in a span with staggered CSS animation delay
      lettersSpan.innerHTML = lettersSpan.textContent.replace(/\S/g, (char, i) => {
        const delay = baseDelayMs + 34 * (i + 1);
        return `<span class='letter' style='animation-delay:${delay}ms'>${char}</span>`;
      });
    };

    // Use requestIdleCallback so this doesn't block LCP
    const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 100));
    schedule(() => {
      animateTextLine(line1Ref, 0);
      animateTextLine(line2Ref, 1400);
      animateTextLine(line3Ref, 2800);
    });
  }, []);

  // Auto-rotate hero messages
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroMessage((prev) => (prev + 1) % heroMessages.length);
    }, 4000); // Change message every 4 seconds

    return () => clearInterval(interval);
  }, [heroMessages.length]);

  // Close reaction picker on outside click
  useEffect(() => {
    const handleClickOutside = () => {
      if (showReactionPicker !== null) {
        setShowReactionPicker(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showReactionPicker]);

  // FAQ data
  // NOTE: To add new FAQ questions and answers, add a new object to this array with 'question' and 'answer' properties
  // The questions will automatically appear in the carousel rotation and can be reacted to with emojis
  // Example:
  // {
  //   question: "Your new question here?",
  //   answer: "Your detailed answer here."
  // }
  const faqData = [
    {
      question: "Which devices are compatible?",
      answer: "iPhone 11+ models, Google Pixel 3+, Samsung Galaxy S20+, and iPad Pro with cellular support eSIM technology."
    },
    {
      question: "How does data sharing work with the Premium plan?",
      answer: "With the Home and Away Premium plan, you'll receive up to 6 different long term eSIM profiles that all draw from the same data pool. Each device has its own unique eSIM, but they all share the same data allocation, making it perfect for families or individuals with multiple devices."
    },
    // REMOVED: Trip eSIM FAQ - may be added back in the future
    // {
    //   question: "What is a Trip eSIM?",
    //   answer: "From your subscription account, you can create short term Trip eSIMs for any device that supports eSIM. These are ideal for other devices, friends or family to share your data plans. After the trip they can be deleted, and their validity is 3 months."
    // },
    {
      question: "Do I need to switch plans when I travel?",
      answer: `No. All our plans work automatically in ${countryCount}+ countries without any need to change settings or switch plans. That's why we call them "Home and Away" plans—they work seamlessly wherever you go.`
    },
    {
      question: "What is flex billing?",
      answer: "Flex billing is an optional feature for subscription plans that automatically charges you at country-specific rates if you exceed your included data allocation. This prevents service interruption and ensures you always stay connected."
    }
  ];

  // Auto-rotate FAQ questions (pause on hover)
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isFaqHovered) {
        setCurrentFaqIndex((prev) => (prev + 1) % faqData.length);
      }
    }, 5000); // Change question every 5 seconds

    return () => clearInterval(interval);
  }, [faqData.length, isFaqHovered]);

  // Auto-show answer 0.5s after question appears
  useEffect(() => {
    setExpandedFaq(null); // Hide answer immediately when question changes
    const timer = setTimeout(() => {
      setExpandedFaq(currentFaqIndex);
    }, 500); // Show answer after 0.5 seconds

    return () => clearTimeout(timer);
  }, [currentFaqIndex]);

  // Handle FAQ click
  const handleFaqClick = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  // Handle reaction selection
  const handleReactionSelect = (index, emoji) => {
    setFaqReactions(prev => ({
      ...prev,
      [index]: emoji
    }));
    setShowReactionPicker(null);
  };

  // Handle answer bubble click
  const handleAnswerClick = (e, index) => {
    e.stopPropagation();
    setShowReactionPicker(showReactionPicker === index ? null : index);
  };

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  // Handle touch start
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  // Handle touch move
  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  // Handle touch end
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      // Swipe left - next question
      setCurrentFaqIndex((prev) => (prev + 1) % faqData.length);
    } else if (isRightSwipe) {
      // Swipe right - previous question
      setCurrentFaqIndex((prev) => (prev - 1 + faqData.length) % faqData.length);
    }
  };

  // Calculate GB amounts based on home country price
  const calculatePremiumGBs = () => Math.round(29 / homeCountryPrice);
  const calculateSoloGBs = () => Math.round(19 / homeCountryPrice);
  const getPayGoGBs = () => `${Math.round(5 / homeCountryPrice)} GB`;

  // Scroll effect: $5 shrinks from 70rem to 8rem as section scrolls into view
  const [fiveBucksProgress, setFiveBucksProgress] = useState(0);
  useEffect(() => {
    if (prefersReducedMotion.current) { setFiveBucksProgress(1); return; }
    let maxProgress = 0;
    let rafId;
    const handleScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        if (!fiveBucksRef.current) return;
        const rect = fiveBucksRef.current.getBoundingClientRect();
        const progress = Math.min(1, Math.max(0, (window.innerHeight - rect.top) / (window.innerHeight * 0.8)));
        maxProgress = Math.max(maxProgress, progress);
        setFiveBucksProgress(maxProgress);
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => { window.removeEventListener('scroll', handleScroll); cancelAnimationFrame(rafId); };
  }, []);

  // Direct $5 PayGo signup — saves purchase state and routes to account or payment step
  const handleFiveBucksSignup = () => {
    purchaseHelpers.savePurchaseState({
      planType: 'pay_as_you_go',
      planId: null,
      creditAmount: 5,
      price: 5,
      autoReload: { enabled: false, threshold: 5, amount: 25 },
      overageSettings: { enabled: false, max_amount: 0 },
      step: 'plan-selection'
    });
    navigate(isAuthenticated() ? '/newuser/payment' : '/newuser/account');
  };

  // Helper to get price for a specific country from the API data (searches all tiers)
  const getCountryPrice = (countryCode) => {
    for (const tier of Object.values(countryPricingData)) {
      const country = tier.countries?.find(c => c.country_code === countryCode);
      if (country) {
        return parseFloat(country.price_per_gb).toFixed(2);
      }
    }
    return null;
  };
  
  return (
    <>
      <DynamicMetaTags
        alternates={[
          { hreflang: 'en-CA', href: 'https://jetogo.com/en-ca' },
          { hreflang: 'en-AU', href: 'https://jetogo.com/en-au' },
          { hreflang: 'en', href: 'https://jetogo.com/' },
          { hreflang: 'x-default', href: 'https://jetogo.com/' },
        ]}
      />
      <Helmet>
        <meta property="og:video" content="https://jetogo.com/assets/video/jetogo-main.webm" />
        <meta property="og:video:type" content="video/webm" />
        <meta property="og:video:width" content="816" />
        <meta property="og:video:height" content="720" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "VideoObject",
          "name": "Jetogo eSIM — One eSIM for Travel, Home & Backup",
          "description": "See how Jetogo connects you in 130+ countries with a single eSIM. No plan switching, no data management — just install once and go.",
          "thumbnailUrl": "https://jetogo.com/assets/video/jetogo-main-poster.webp",
          "contentUrl": "https://jetogo.com/assets/video/jetogo-main.webm",
          "uploadDate": "2026-03-23T00:00:00+00:00",
          "duration": "PT11S",
          "embedUrl": "https://jetogo.com/"
        })}</script>
      </Helmet>
      <style>{`
        .text-wrapper {
          display: block;
        }
        .text-wrapper .letter {
          display: inline-block;
          opacity: 0;
          animation: letter-fade-in 0.6s cubic-bezier(0.19, 1, 0.22, 1) forwards;
        }
        @keyframes letter-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-in {
          0% {
            opacity: 0;
            transform: scale(0.8);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-scale-in {
          animation: scale-in 0.2s ease-out;
        }
      `}</style>
      <div className="home-page bg-primary transition-colors duration-200" data-page="home">
        {/* Business Plans Banner */}
        <Link
          to="/landing/business-plans"
          className="block bg-[#1a1a2e] text-white text-center py-2.5 px-4 text-sm hover:bg-[#16213e] transition-colors duration-200"
        >
          <span className="font-semibold text-[#9cc5a1]">New:</span>{' '}
          Business Plans for Teams{' '}
          <HiArrowRight className="inline-block ml-1 w-4 h-4" />
        </Link>
        {/* Hero Section */}
      <div className="relative overflow-hidden transition-colors duration-200" style={{ background: 'linear-gradient(to bottom right, #216869, #49a078)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 md:pr-12 order-2 md:order-1">
              <h1 className="text-4xl md:text-5xl mb-4 leading-tight text-white transition-colors duration-200" style={{ fontWeight: 800, fontFamily: 'Panton' }}>
                One eSIM for Travel, Home <span className="text-[#9cc5a1]">&</span> Backup
              </h1>
              <div className="text-xl font-semibold mb-8 text-white/90 transition-colors duration-200 space-y-2">
                <div ref={line1Ref} className="text-wrapper">
                  <span className="letters inline-block">Stop choosing data plans and deleting old eSIMs.</span>
                </div>
                <div ref={line2Ref} className="text-wrapper">
                  <span className="letters inline-block">Credit never expires.</span>
                </div>
                <div ref={line3Ref} className="text-wrapper">
                  <span className="letters inline-block">Works globally.</span>
                </div>
              </div>
              {/* Rotating hero messages */}
              <div className="mt-6">
                <div className="h-20 relative mb-2">
                  {heroMessages.map((message, index) => (
                    <div
                      key={index}
                      className={`absolute inset-0 transition-opacity duration-500 ${
                        currentHeroMessage === index ? 'opacity-100' : 'opacity-0'
                      }`}
                    >
                      <p className="text-sm text-white/90">
                        <span className="font-extrabold text-white block text-xl mb-1">{message.title}</span>
                        {message.text}
                      </p>
                    </div>
                  ))}
                </div>
                {/* Navigation dots */}
                <div className="flex gap-2">
                  {heroMessages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentHeroMessage(index)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        currentHeroMessage === index
                          ? 'bg-white w-4'
                          : 'bg-white/40 hover:bg-white/60'
                      }`}
                      aria-label={`Go to message ${index + 1}`}
                    />
                  ))}
                </div>
              </div>

            </div>
            <div className="md:w-1/2 mb-10 md:mb-0 order-1 md:order-2">
              <video
                ref={heroVideoRef}
                autoPlay={!prefersReducedMotion.current && shouldAutoplay.current}
                loop
                muted
                playsInline
                preload="metadata"
                poster="/assets/video/jetogo-main-poster.webp"
                aria-label="Jetogo eSIM app showing connectivity in 130+ countries"
                width="448"
                height="448"
                className="w-full h-auto max-w-md mx-auto rounded-2xl"
                fetchpriority="high"
              >
                <source src="/assets/video/jetogo-main-hevc.mov" type='video/mp4; codecs="hvc1"' />
                <source src="/assets/video/jetogo-main.webm" type="video/webm" />
              </video>
            </div>
          </div>
        </div>
      </div>

      {/* 5 Bucks Section */}
      <div className="py-16 bg-gray-100 dark:bg-gray-900 transition-colors duration-200">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center gap-10 max-w-5xl mx-auto">

            {/* Right: Bullet points + CTA */}
            <div className="md:w-1/2 order-2 md:order-2">
              <h2 className="text-4xl md:text-5xl font-bold text-primary transition-colors duration-200 mb-6" style={{ fontFamily: 'Panton' }}>
  Start for just <span className="text-[#49a078]">$5</span>
              </h2>

              <ul className="space-y-4 mb-8">
                <li className="flex items-center gap-3">
                  <svg className="w-6 h-6 text-[#49a078] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <Link to="/refund-policy" className="text-lg text-primary no-underline hover:text-[#49a078]">Money-back guarantee</Link>
                </li>
                <li className="flex items-center gap-3">
                  <svg className="w-6 h-6 text-[#49a078] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span className="text-lg text-primary">Use on 4 different devices</span>
                </li>
                <li className="flex items-center gap-3">
                  <svg className="w-6 h-6 text-[#49a078] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span className="text-lg text-primary">No commitments</span>
                </li>
                <li className="flex items-center gap-3">
                  <svg className="w-6 h-6 text-[#49a078] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span className="text-lg text-primary">Credit never expires</span>
                </li>
                <li className="flex items-center gap-3">
                  <svg className="w-6 h-6 text-[#49a078] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span className="text-lg text-primary">USA IP in US</span>
                </li>
                <li className="flex items-center gap-3">
                  <svg className="w-6 h-6 text-[#49a078] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span className="text-lg text-primary">Limited time: New user 5 GB bonus</span>
                </li>
              </ul>

              <button
                onClick={handleFiveBucksSignup}
                className="px-10 py-4 rounded-full bg-gradient-to-r from-[#3d8a66] to-[#5cb88a] text-white text-lg font-semibold hover:from-[#357a5a] hover:to-[#4fa87a] transition-all shadow-lg hover:shadow-xl cursor-pointer"
              >
                Try risk-free &raquo;
              </button>
            </div>

            {/* Left: Image with $5 overlay */}
            <div className="md:w-1/2 order-1 md:order-1 flex justify-center">
              <div ref={fiveBucksRef} className="relative w-64 md:w-64 overflow-hidden rounded-2xl cursor-pointer" onClick={handleFiveBucksSignup}>
                <img
                  src={`/assets/images/homepage/5-${fiveBucksImage}.webp`}
                  alt="Happy traveler connected with Jetogo eSIM"
                  className="w-full rounded-2xl"
                  loading="lazy"
                />
                <span
                  className="absolute bottom-4 left-4 font-bold text-white drop-shadow-[0_6px_12px_rgba(0,0,0,0.6)] pointer-events-none"
                  style={{
                    fontFamily: 'Panton',
                    fontSize: '8rem',
                    lineHeight: 1,
                    transformOrigin: 'bottom left',
                    transform: `scale(${8.75 - fiveBucksProgress * 7.75})`,
                  }}
                >$5</span>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Why Choose Jetogo eSIM Section - Timeline Style */}
      <div className="py-20 bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
        <div className="container mx-auto px-4 md:px-8">
          <h2 className="text-3xl font-bold text-center mb-16 text-primary transition-colors duration-200">
            Why Choose Jetogo eSIM?
          </h2>

          <div className="max-w-4xl mx-auto relative">
            {/* Vertical Timeline Line */}
            <div className="absolute left-0 md:left-1/4 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 via-green-500 to-purple-500 transform -translate-x-1/2 hidden md:block" />

            {/* Reason 1: Effortless */}
            <div
              className="reason-item opacity-0 translate-y-8 transition-all duration-700 ease-out mb-16"
              ref={(el) => {
                if (el) {
                  const observer = new IntersectionObserver(
                    ([entry]) => {
                      if (entry.isIntersecting) {
                        el.classList.remove('opacity-0', 'translate-y-8');
                        el.classList.add('opacity-100', 'translate-y-0');
                      }
                    },
                    { threshold: 0.2 }
                  );
                  observer.observe(el);
                }
              }}
            >
              <div className="flex flex-col md:flex-row items-start gap-6">
                {/* Left: Reason Label */}
                <div className="md:w-1/4 flex items-center gap-3">
                  <div className="w-4 h-4 rounded-full bg-blue-500 ring-4 ring-blue-200 dark:ring-blue-900 flex-shrink-0 hidden md:block" />
                  <span className="text-sm font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider">
                    Reason 1
                  </span>
                </div>
                {/* Right: Content */}
                <div className="md:w-3/4 bg-accent rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
                  <h3 className="text-2xl font-bold text-primary mb-2">It's Effortless</h3>
                  <p className="text-lg font-semibold text-blue-600 dark:text-blue-400 mb-4">Connected in 3 Simple Steps</p>
                  <p className="text-secondary mb-4">Forget choosing complex plans or signing contracts.</p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center text-blue-600 font-bold text-sm">1</span>
                      <div>
                        <span className="font-semibold text-primary">Top Up:</span>
                        <span className="text-secondary ml-1">Load your wallet (e.g., $5).</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center text-blue-600 font-bold text-sm">2</span>
                      <div>
                        <span className="font-semibold text-primary">Activate:</span>
                        <span className="text-secondary ml-1">Install the QR code once.</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center text-blue-600 font-bold text-sm">3</span>
                      <div>
                        <span className="font-semibold text-primary">Connect:</span>
                        <span className="text-secondary ml-1">Land in 130+ countries and you're online.</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Reason 2: Fair & Affordable */}
            <div
              className="reason-item opacity-0 translate-y-8 transition-all duration-700 ease-out mb-16"
              ref={(el) => {
                if (el) {
                  const observer = new IntersectionObserver(
                    ([entry]) => {
                      if (entry.isIntersecting) {
                        el.classList.remove('opacity-0', 'translate-y-8');
                        el.classList.add('opacity-100', 'translate-y-0');
                      }
                    },
                    { threshold: 0.2 }
                  );
                  observer.observe(el);
                }
              }}
            >
              <div className="flex flex-col md:flex-row items-start gap-6">
                {/* Left: Reason Label */}
                <div className="md:w-1/4 flex items-center gap-3">
                  <div className="w-4 h-4 rounded-full bg-green-500 ring-4 ring-green-200 dark:ring-green-900 flex-shrink-0 hidden md:block" />
                  <span className="text-sm font-semibold text-green-600 dark:text-green-400 uppercase tracking-wider">
                    Reason 2
                  </span>
                </div>
                {/* Right: Content */}
                <div className="md:w-3/4 bg-accent rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
                  <h3 className="text-2xl font-bold text-primary mb-2">It's Fair & Affordable</h3>
                  <p className="text-lg font-semibold text-green-600 dark:text-green-400 mb-4">Stop Overpaying for Roaming & Wasted Data</p>
                  <p className="text-secondary mb-4">
                    Enjoy flat, near-local rates. Unlike traditional plans that expire after a fixed period,
                    Jetogo works like a data wallet. Used only $2 worth of data? The remaining credit waits
                    for your next trip, forever.
                  </p>
                  <p className="text-sm text-tertiary italic">*Subscription plans have credit rollover period.</p>
                </div>
              </div>
            </div>

            {/* Reason 3: Safety Net */}
            <div
              className="reason-item opacity-0 translate-y-8 transition-all duration-700 ease-out"
              ref={(el) => {
                if (el) {
                  const observer = new IntersectionObserver(
                    ([entry]) => {
                      if (entry.isIntersecting) {
                        el.classList.remove('opacity-0', 'translate-y-8');
                        el.classList.add('opacity-100', 'translate-y-0');
                      }
                    },
                    { threshold: 0.2 }
                  );
                  observer.observe(el);
                }
              }}
            >
              <div className="flex flex-col md:flex-row items-start gap-6">
                {/* Left: Reason Label */}
                <div className="md:w-1/4 flex items-center gap-3">
                  <div className="w-4 h-4 rounded-full bg-purple-500 ring-4 ring-purple-200 dark:ring-purple-900 flex-shrink-0 hidden md:block" />
                  <span className="text-sm font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wider">
                    Reason 3
                  </span>
                </div>
                {/* Right: Content */}
                <div className="md:w-3/4 bg-accent rounded-2xl overflow-hidden shadow-lg border border-gray-200 dark:border-gray-700">
                  <img
                    src="/assets/images/why/backup.png"
                    alt="Jetogo as backup connectivity"
                    width="800"
                    height="450"
                    className="w-full h-auto"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-primary mb-2">It's Your Safety Net</h3>
                    <p className="text-lg font-semibold text-purple-600 dark:text-purple-400 mb-4">The Perfect Backup for Home</p>
                    <p className="text-secondary">
                      Wi-Fi down? Working from a coffee shop? Running low on monthly data? Keep Jetogo as a
                      permanent backup and switch it on instantly when your local internet fails.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Video Demo Section */}
      <div className="py-16 bg-primary">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center gap-8 max-w-6xl mx-auto">
            <div className="md:w-1/2">
              <h2 className="text-3xl font-bold mb-4 text-primary">Dual eSIM devices</h2>
              <p className="text-xl text-secondary mb-6">
                Jetogo data only eSIM works perfectly with your unlocked device's second eSIM slot.
              </p>
            </div>
            <div className="md:w-1/2 flex justify-center">
              <div className="relative rounded-2xl overflow-hidden bg-primary w-[300px] h-[300px]">
                {useGif && hasPlayedOnce ? (
                  <img
                    src={videoSrc}
                    alt="Dual eSIM Device"
                    width="300"
                    height="300"
                    className="w-full h-full object-contain"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <video
                    ref={videoRef}
                    key={videoSrc}
                    className="w-full h-full object-contain"
                    loop={hasPlayedOnce}
                    muted
                    playsInline
                  >
                    <source src={videoSrc} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Use at Home / Away Section - Split View with Reveal Slider */}
      <HomeAwayRevealSection />

      {/* Featured Plans — ref used to trigger lazy pricing data load */}
      <div ref={pricingSectionRef} className="py-16 bg-gray-100 dark:bg-gray-900 transition-colors duration-200">
        <div className="container mx-auto px-4 md:px-8">
          <h2 className="text-3xl font-bold text-center mb-12 text-primary transition-colors duration-200">Our eSIM Plans</h2>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* Pay as You Go Card */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border-l-4 border-l-[#49a078] border border-gray-200 dark:border-gray-700 p-6 flex flex-col">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4" style={{ fontFamily: 'Panton' }}>Pay as You Go</h2>

              <div className="mb-1">
                <span className="text-3xl font-bold text-[#49a078]">$5</span>
                <span className="text-gray-500 dark:text-gray-400"> to start</span>
              </div>

              {!pricingLoading && (
                <div className="mb-6">
                  <button
                    type="button"
                    className="cursor-pointer hover:opacity-80 text-left focus:outline-none focus:ring-2 focus:ring-[#49a078] focus:ring-offset-2 rounded"
                    onClick={() => setShowPayGoDrawer(true)}
                  >
                    <span className="text-xl font-bold text-[#49a078]">{getPayGoGBs()}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">in {homeCountryName}</span>
                  </button>
                  <button
                    type="button"
                    className="flex items-center mt-1 cursor-pointer hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-[#49a078] focus:ring-offset-2 rounded"
                    onClick={() => setShowPayGoBonusDrawer(true)}
                  >
                    <span className="text-sm text-[#49a078] font-semibold">+ 5GB one-time</span>
                    <span className="text-sm text-gray-600 dark:text-gray-400 ml-1">bonus</span>
                  </button>
                </div>
              )}

              <Link to="/newplan/paygo" className="w-full mb-6">
                <button className="w-full py-3 rounded-full bg-gradient-to-r from-[#3d8a66] to-[#5cb88a] text-white font-semibold hover:from-[#357a5a] hover:to-[#4fa87a] transition-all">
                  Choose Pay as You Go
                </button>
              </Link>

              <ul className="space-y-3 mb-6 flex-1">
                <li className="flex items-center gap-3">
                  <HiOutlineChip className="w-5 h-5 text-[#49a078] flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">No monthly commitment</span>
                </li>
                <li className="flex items-center gap-3">
                  <HiOutlineGlobe className="w-5 h-5 text-[#49a078] flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">Global coverage in {countryCount}+ countries</span>
                </li>
                <li className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-[#49a078] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.178 8c5.096 0 5.096 8 0 8-5.095 0-7.133-8-12.739-8-4.695 0-4.695 8 0 8 5.606 0 7.644-8 12.74-8z" />
                  </svg>
                  <span className="text-gray-700 dark:text-gray-300">Credit never expires</span>
                </li>
              </ul>

              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs font-bold text-[#49a078] uppercase tracking-wider mb-2">WHO'S IT BEST FOR</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Want to travel abroad a few times a year for less than your home carrier? Need backup data? Want to pay only when you use it? Good option for casual users.</p>
              </div>
            </div>

            {/* Solo Plan Card */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 flex flex-col relative">
              <span className="absolute -top-3 left-6 bg-[#49a078] text-white text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider">
                Best Value
              </span>

              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4" style={{ fontFamily: 'Panton' }}>Solo</h2>

              <div className="mb-1">
                <span className="text-3xl font-bold text-[#49a078]">$19.99</span>
                <span className="text-gray-500 dark:text-gray-400">/month</span>
              </div>

              {!pricingLoading && (
                <div className="mb-6">
                  <button
                    type="button"
                    className="cursor-pointer hover:opacity-80 text-left focus:outline-none focus:ring-2 focus:ring-[#49a078] focus:ring-offset-2 rounded"
                    onClick={() => setShowSoloDrawer(true)}
                  >
                    <span className="text-xl font-bold text-[#49a078]">{calculateSoloGBs()} GB</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">per month</span>
                  </button>
                  <button
                    type="button"
                    className="flex items-center mt-1 cursor-pointer hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-[#49a078] focus:ring-offset-2 rounded"
                    onClick={() => setShowSoloBonusDrawer(true)}
                  >
                    <span className="text-sm text-[#49a078] font-semibold">+ 30GB</span>
                    <span className="text-sm text-gray-600 dark:text-gray-400 ml-1">bonus</span>
                  </button>
                </div>
              )}

              <Link to="/newplan/solo" className="w-full mb-6">
                <button className="w-full py-3 rounded-full border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold hover:border-[#49a078] hover:text-[#49a078] transition-colors">
                  Choose Solo
                </button>
              </Link>

              <ul className="space-y-3 mb-6 flex-1">
                <li className="flex items-center gap-3">
                  <HiOutlineChip className="w-5 h-5 text-[#49a078] flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">Up to 2 eSIMs included</span>
                </li>
                <li className="flex items-center gap-3">
                  <HiOutlineGlobe className="w-5 h-5 text-[#49a078] flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">Global coverage in {countryCount}+ countries</span>
                </li>
                <li className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-[#49a078] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.178 8c5.096 0 5.096 8 0 8-5.095 0-7.133-8-12.739-8-4.695 0-4.695 8 0 8 5.606 0 7.644-8 12.74-8z" />
                  </svg>
                  <span className="text-gray-700 dark:text-gray-300">3-month data rollover</span>
                </li>
              </ul>

              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs font-bold text-[#49a078] uppercase tracking-wider mb-2">WHO'S IT BEST FOR</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Perfect for individuals who use a passport regularly to cross borders and want the convenience of one monthly bill with no surprise cutoffs.</p>
              </div>
            </div>

            {/* Premium Plan Card */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 flex flex-col">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4" style={{ fontFamily: 'Panton' }}>Premium</h2>

              <div className="mb-1">
                <span className="text-3xl font-bold text-[#49a078]">$29.99</span>
                <span className="text-gray-500 dark:text-gray-400">/month</span>
              </div>

              {!pricingLoading && (
                <div className="mb-6">
                  <button
                    type="button"
                    className="cursor-pointer hover:opacity-80 text-left focus:outline-none focus:ring-2 focus:ring-[#49a078] focus:ring-offset-2 rounded"
                    onClick={() => setShowPremiumDrawer(true)}
                  >
                    <span className="text-xl font-bold text-[#49a078]">{calculatePremiumGBs()} GB</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">per month</span>
                  </button>
                  <button
                    type="button"
                    className="flex items-center mt-1 cursor-pointer hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-[#49a078] focus:ring-offset-2 rounded"
                    onClick={() => setShowPremiumBonusDrawer(true)}
                  >
                    <span className="text-sm text-[#49a078] font-semibold">+ 120GB</span>
                    <span className="text-sm text-gray-600 dark:text-gray-400 ml-1">bonus</span>
                  </button>
                </div>
              )}

              <Link to="/newplan/premium" className="w-full mb-6">
                <button className="w-full py-3 rounded-full border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold hover:border-[#49a078] hover:text-[#49a078] transition-colors">
                  Choose Premium
                </button>
              </Link>

              <ul className="space-y-3 mb-6 flex-1">
                <li className="flex items-center gap-3">
                  <HiOutlineChip className="w-5 h-5 text-[#49a078] flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">Up to 6 eSIMs included</span>
                </li>
                <li className="flex items-center gap-3">
                  <HiOutlineGlobe className="w-5 h-5 text-[#49a078] flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">Global coverage in {countryCount}+ countries</span>
                </li>
                <li className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-[#49a078] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.178 8c5.096 0 5.096 8 0 8-5.095 0-7.133-8-12.739-8-4.695 0-4.695 8 0 8 5.606 0 7.644-8 12.74-8z" />
                  </svg>
                  <span className="text-gray-700 dark:text-gray-300">4-month data rollover</span>
                </li>
              </ul>

              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs font-bold text-[#49a078] uppercase tracking-wider mb-2">WHO'S IT BEST FOR</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Regularly traveling abroad with family or team? Do you like having a backup mobile data option? Manage up to 6 devices on one bill.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Country Coverage by Pricing Group Section */}
      <div className="py-12 bg-white dark:bg-gray-900 transition-colors duration-200">
        <div className="container mx-auto px-4 md:px-8">
          <h2 className="text-3xl text-center mb-10 text-primary transition-colors duration-200" style={{fontWeight: 700, fontFamily: 'Panton'}}>
            Pay For Usage, at <span className="text-[#49a078]">Reasonable Rates</span>
          </h2>

          {/* Featured Country Cards - 3x2 Grid with button in last spot */}
          {!pricingLoading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto mb-8">
              {(() => {
                // Default countries if inference not ready
                const defaultCountries = [
                  { countryCode: 'SG', countryName: 'Singapore', reasons: ['Great time to visit'] },
                  { countryCode: 'CN', countryName: 'China', reasons: ['Great time to visit'] },
                  { countryCode: 'TH', countryName: 'Thailand', reasons: ['Great time to visit'] },
                  { countryCode: 'KR', countryName: 'South Korea', reasons: ['Great time to visit'] },
                  { countryCode: 'JP', countryName: 'Japan', reasons: ['Great time to visit'] },
                ];

                // Use inferred countries if available (at least 3), otherwise use defaults
                const displayCountries = inferredCountries.length >= 3
                  ? inferredCountries.slice(0, 5)
                  : defaultCountries;

                return (
                  <>
                    {displayCountries.map((country) => {
                      const price = getCountryPrice(country.countryCode);
                      const displayReason = country.reasons?.[0] || 'Great time to visit';

                      return (
                        <div
                          key={country.countryCode}
                          className="bg-gray-50 dark:bg-gray-800 rounded-xl px-4 py-3 flex items-center gap-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                        >
                          <span className="text-4xl">{getFlagEmoji(country.countryCode)}</span>
                          <div>
                            <h3 className="text-base font-bold text-gray-900 dark:text-white">{country.countryName}</h3>
                            <div className="flex items-baseline">
                              <span className="text-lg font-bold text-[#49a078]">
                                ${price || '—'}
                              </span>
                              <span className="text-sm text-gray-500 dark:text-gray-400 ml-0.5">/GB</span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{displayReason}</p>
                          </div>
                        </div>
                      );
                    })}
                    {/* Button in last grid spot */}
                    <Link
                      to="/countries"
                      className="rounded-xl px-4 py-3 flex items-center justify-center text-white font-medium hover:opacity-90 transition-opacity duration-200 bg-[#49a078]"
                    >
                      <span>See all countries & pricing</span>
                      <HiArrowRight className="w-5 h-5 ml-2" />
                    </Link>
                  </>
                );
              })()}
            </div>
          )}

          {pricingLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
              <p className="text-secondary">Loading country pricing data...</p>
            </div>
          ) : (
            <div className="max-w-5xl mx-auto">
              {/* Pricing Groups Sub-header */}
              <h3 className="text-2xl font-bold text-center mb-8 text-primary" style={{fontFamily: 'Panton'}}>
                Pricing Groups
              </h3>

              {/* Two Column Layout: Vertical Group List + Country Grid */}
              <div className="flex flex-col lg:flex-row gap-6 lg:items-stretch">
                {/* Left: Vertical Group List */}
                <div className="lg:w-80 flex-shrink-0 flex flex-col">
                  <div className="space-y-3 flex-1">
                    {(() => {
                      const allTiers = Object.keys(countryPricingData).sort((a, b) => Number(a) - Number(b));

                      if (allTiers.length === 0) {
                        return (
                          <div className="text-center text-gray-500 dark:text-gray-400 py-4">
                            <p>Unable to load pricing groups.</p>
                            <p className="text-sm mt-1">Please check your connection.</p>
                          </div>
                        );
                      }

                      const visibleTiers = allTiers.slice(visibleGroupStart, visibleGroupStart + 4);

                      return visibleTiers.map(tier => {
                        const tierInfo = countryPricingData[tier];
                        const minPrice = Math.round(tierInfo.minPrice * 100) / 100;
                        const maxPrice = Math.round(tierInfo.maxPrice * 100) / 100;
                        const priceRange = minPrice === maxPrice
                          ? `$${minPrice.toFixed(2)}`
                          : `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`;

                        return (
                          <button
                            key={tier}
                            onClick={() => setSelectedTier(Number(tier))}
                            className={`w-full text-left rounded-xl px-4 py-2.5 transition-all duration-200 border-2 ${
                              selectedTier === Number(tier)
                                ? 'bg-white dark:bg-gray-800 border-l-4 border-l-[#49a078] border-t-gray-200 dark:border-t-gray-600 border-r-gray-200 dark:border-r-gray-600 border-b-gray-200 dark:border-b-gray-600 shadow-md'
                                : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                            }`}
                          >
                            <div className="text-xs text-gray-500 dark:text-gray-400" style={{fontFamily: 'Panton'}}>Group {tier}</div>
                            <div className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                              {priceRange}<span className="text-xs font-normal text-gray-500 dark:text-gray-400">/GB</span>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {tierInfo.countries.length} {tierInfo.countries.length === 1 ? 'country' : 'countries'}
                            </div>
                          </button>
                        );
                      });
                    })()}
                  </div>

                  {/* View More Button */}
                  {Object.keys(countryPricingData).length > 4 && (
                    <button
                      onClick={() => {
                        const allTiers = Object.keys(countryPricingData);
                        const nextStart = visibleGroupStart + 4;
                        setVisibleGroupStart(nextStart >= allTiers.length ? 0 : nextStart);
                      }}
                      className="w-full mt-4 py-3 rounded-xl text-white font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: '#49a078' }}
                    >
                      <span>{visibleGroupStart + 4 >= Object.keys(countryPricingData).length ? 'Back to Top' : 'View More'}</span>
                      <svg className={`w-4 h-4 transition-transform ${visibleGroupStart + 4 >= Object.keys(countryPricingData).length ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  )}
                </div>

                {/* Right: Country Grid for Selected Tier */}
                <div className="flex-1 bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  {countryPricingData[selectedTier] ? (
                    <>
                      <div className="flex items-center gap-3 mb-6">
                        <h4 className="text-xl font-bold text-gray-900 dark:text-white" style={{fontFamily: 'Panton'}}>
                          Group {selectedTier} Countries
                        </h4>
                        <span className="px-3 py-1 rounded-full text-white text-sm font-medium bg-[#49a078]">
                          {countryPricingData[selectedTier].countries.length} Total
                        </span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-y-3 gap-x-4">
                        {countryPricingData[selectedTier].countries.map(country => (
                          <div
                            key={country.country_code}
                            className="flex items-center gap-2"
                          >
                            <span className="text-2xl flex-shrink-0">{getFlagEmoji(country.country_code)}</span>
                            <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{country.country}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                      <p>No countries found for this tier</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Features Section - With a Jetogo Account */}
      <div className="py-16 bg-accent transition-colors duration-200">
        <div className="container mx-auto px-4 md:px-8">
          <h2 className="text-3xl font-bold text-center mb-12 text-primary transition-colors duration-200">With a Jetogo Account</h2>

          <div className="grid md:grid-cols-3 gap-x-8 gap-y-12 max-w-5xl mx-auto">
            {/* Instant Activation */}
            <div className="group relative flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-4 cursor-pointer group-hover:ring-4 group-hover:ring-blue-200 transition-all duration-300">
                <img src="/assets/images/icons/ios_esim.svg" alt="eSIM" className="w-8 h-8" loading="lazy" decoding="async" />
              </div>
              <h3 className="text-xl font-bold text-primary transition-colors duration-200">
                Instant Activation
              </h3>
              {/* Animated Tooltip - Top */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 z-20">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-4 border border-blue-200 dark:border-blue-800 relative">
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-white dark:border-t-gray-800" />
                  <p className="text-sm text-secondary">Activate your eSIM in minutes with a simple QR code scan</p>
                </div>
              </div>
            </div>

            {/* Multiple Devices */}
            <div className="group relative flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4 cursor-pointer group-hover:ring-4 group-hover:ring-green-200 transition-all duration-300">
                <HiOutlineDeviceMobile className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-primary transition-colors duration-200">
                Multiple Devices
              </h3>
              {/* Animated Tooltip - Top */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 z-20">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-4 border border-green-200 dark:border-green-800 relative">
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-white dark:border-t-gray-800" />
                  <p className="text-sm text-secondary">Share your data across multiple devices in one account</p>
                </div>
              </div>
            </div>

            {/* Add & Remove eSIMs */}
            <div className="group relative flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mb-4 cursor-pointer group-hover:ring-4 group-hover:ring-yellow-200 transition-all duration-300">
                <HiOutlineChip className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="text-xl font-bold text-primary transition-colors duration-200">
                Add & Remove eSIMs
              </h3>
              {/* Animated Tooltip - Top */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 z-20">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-4 border border-yellow-200 dark:border-yellow-800 relative">
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-white dark:border-t-gray-800" />
                  <p className="text-sm text-secondary">You can add or delete an eSIM any time. Get a new phone? No problem, issue a new eSIM.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <button
              type="button"
              className="inline-flex items-center px-8 py-3 bg-[#49a078] hover:bg-[#3d8a66] text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#49a078] focus:ring-offset-2"
              onClick={() => pricingSectionRef.current?.scrollIntoView({ behavior: 'smooth' })}
            >
              Pick a Plan
            </button>
          </div>
        </div>
      </div>

      {/* Feature Comparison Section */}
      <div id="comparison" className="py-16">
        <div className="container mx-auto px-4 md:px-8">
          <h2 className="text-3xl font-bold text-center mb-12 text-primary transition-colors duration-200">Detailed Comparison</h2>

          {/* Mobile Card View */}
          <div className="block md:hidden space-y-6 max-w-lg mx-auto">
            {/* Pay as You Go Card */}
            <Card className="bg-tertiary border-2 border-green-200 dark:border-green-800">
              <h3 className="text-xl font-bold text-green-600 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
                Pay as You Go
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">Price</span>
                  <span className="text-primary">$5-$50 credit options</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">Data</span>
                  <div className="text-right">
                    <button
                      type="button"
                      className="font-medium text-green-600 cursor-pointer hover:text-green-700 underline focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 rounded"
                      onClick={() => setShowPayGoDrawer(true)}
                    >
                      {getPayGoGBs()} in {homeCountryName}
                    </button>
                    <button
                      type="button"
                      className="flex items-center justify-end mt-1 cursor-pointer hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 rounded"
                      onClick={() => setShowPayGoBonusDrawer(true)}
                    >
                      <span className="text-xs text-primary">+ <span className="font-bold">5GB one-time bonus</span></span>
                      <HiInformationCircle className="text-green-400 ml-1 flex-shrink-0 w-3 h-3" aria-hidden="true" />
                    </button>
                  </div>
                </div>
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">Data Rollover</span>
                  <span className="text-primary">Never expires</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">Billing Style</span>
                  <span className="text-primary">Pay as needed</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">eSIMs</span>
                  <span className="text-primary">Up to 4</span>
                </div>
                {/* REMOVED: Trip eSIM feature - may be added back in the future
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">Trip eSIMs</span>
                  <span className="text-primary">No</span>
                </div>
                */}
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">Hotspot Sharing</span>
                  <span className="text-green-500">✓</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">Ideal For</span>
                  <span className="text-primary text-right">Occasional travelers</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">Flex Billing</span>
                  <span className="text-red-500">Not applicable</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">Data Speeds</span>
                  <span className="text-purple-500">No Throttling</span>
                </div>
              </div>
              <Link to="/newplan/paygo" className="block mt-4">
                <button type="button" className="w-full py-2 border-2 border-[#49a078] text-[#49a078] hover:bg-[#49a078] hover:text-white font-semibold rounded-lg transition-colors duration-200 text-sm">Get this plan</button>
              </Link>
            </Card>

            {/* Solo Card */}
            <Card className="bg-tertiary border-2 border-blue-200 dark:border-blue-800">
              <h3 className="text-xl font-bold text-blue-600 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
                Home and Away Solo
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">Price</span>
                  <span className="text-primary">$19.99/month</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">Data</span>
                  <div className="text-right">
                    <button
                      type="button"
                      className="font-medium text-blue-600 cursor-pointer hover:text-blue-700 underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
                      onClick={() => setShowSoloDrawer(true)}
                    >
                      {calculateSoloGBs()} GB
                    </button>
                    <button
                      type="button"
                      className="flex items-center justify-end mt-1 cursor-pointer hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
                      onClick={() => setShowSoloBonusDrawer(true)}
                    >
                      <span className="text-xs text-primary">+ <span className="font-bold">30GB bonus</span></span>
                      <HiInformationCircle className="text-blue-400 ml-1 flex-shrink-0 w-3 h-3" aria-hidden="true" />
                    </button>
                  </div>
                </div>
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">Data Rollover</span>
                  <span className="text-primary">3 months</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">Billing Style</span>
                  <span className="text-primary">Monthly subscription</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">eSIMs</span>
                  <span className="text-primary">Up to 2</span>
                </div>
                {/* REMOVED: Trip eSIM feature - may be added back in the future
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">Trip eSIMs</span>
                  <Link to="/faq" className="text-blue-600 hover:underline">Yes</Link>
                </div>
                */}
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">Hotspot Sharing</span>
                  <span className="text-green-500">✓</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">Ideal For</span>
                  <span className="text-primary text-right">Individual users</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">Flex Billing</span>
                  <Link to="/faq" className="text-blue-600 hover:underline">Optional</Link>
                </div>
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">Data Speeds</span>
                  <Link to="/faq" className="text-purple-600 hover:underline">No Throttling</Link>
                </div>
              </div>
              <Link to="/newplan/solo" className="block mt-4">
                <button type="button" className="w-full py-2 border-2 border-[#49a078] text-[#49a078] hover:bg-[#49a078] hover:text-white font-semibold rounded-lg transition-colors duration-200 text-sm">Get this plan</button>
              </Link>
            </Card>

            {/* Premium Card */}
            <Card className="bg-tertiary border-2 border-purple-200 dark:border-purple-800">
              <h3 className="text-xl font-bold text-purple-600 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
                Home and Away Premium
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">Price</span>
                  <span className="text-primary">$29.99/month</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">Data</span>
                  <div className="text-right">
                    <button
                      type="button"
                      className="font-medium text-purple-600 cursor-pointer hover:text-purple-700 underline focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 rounded"
                      onClick={() => setShowPremiumDrawer(true)}
                    >
                      {calculatePremiumGBs()} GB
                    </button>
                    <button
                      type="button"
                      className="flex items-center justify-end mt-1 cursor-pointer hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 rounded"
                      onClick={() => setShowPremiumBonusDrawer(true)}
                    >
                      <span className="text-xs text-primary">+ <span className="font-bold">120GB bonus</span></span>
                      <HiInformationCircle className="text-accent/70 ml-1 flex-shrink-0 w-3 h-3" aria-hidden="true" />
                    </button>
                  </div>
                </div>
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">Data Rollover</span>
                  <span className="text-primary">4 months</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">Billing Style</span>
                  <span className="text-primary">Monthly subscription</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">eSIMs</span>
                  <span className="text-primary">Up to 6</span>
                </div>
                {/* REMOVED: Trip eSIM feature - may be added back in the future
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">Trip eSIMs</span>
                  <Link to="/faq" className="text-blue-600 hover:underline">Yes</Link>
                </div>
                */}
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">Hotspot Sharing</span>
                  <span className="text-green-500">✓</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">Ideal For</span>
                  <span className="text-primary text-right">Families, multi-device users</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">Flex Billing</span>
                  <Link to="/faq" className="text-blue-600 hover:underline">Optional</Link>
                </div>
                <div className="flex justify-between py-2">
                  <span className="font-medium text-secondary">Data Speeds</span>
                  <Link to="/faq" className="text-purple-600 hover:underline">No Throttling</Link>
                </div>
              </div>
              <Link to="/newplan/premium" className="block mt-4">
                <button type="button" className="w-full py-2 border-2 border-[#49a078] text-[#49a078] hover:bg-[#49a078] hover:text-white font-semibold rounded-lg transition-colors duration-200 text-sm">Get this plan</button>
              </Link>
            </Card>
          </div>

          {/* Tablet/Desktop Table View */}
          <div className="hidden md:block max-w-5xl mx-auto">
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="py-4 px-6 font-semibold text-gray-700 dark:text-gray-300">Feature</th>
                    <th className="py-4 px-6 text-center font-semibold text-gray-700 dark:text-gray-300">Pay as You Go</th>
                    <th className="py-4 px-6 text-center font-semibold text-gray-700 dark:text-gray-300">Home and Away Solo</th>
                    <th className="py-4 px-6 text-center font-semibold text-gray-700 dark:text-gray-300">Home and Away Premium</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-gray-50 dark:bg-gray-700/50">
                    <td className="py-4 px-6 font-semibold text-gray-900 dark:text-white">Price</td>
                    <td className="py-4 px-6 text-center">
                      <span className="text-orange-500 dark:text-orange-400 font-semibold">$5-$50</span>
                      <span className="text-gray-600 dark:text-gray-400"> credit options</span>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <span className="text-orange-500 dark:text-orange-400 font-semibold">$19.99</span>
                      <span className="text-gray-600 dark:text-gray-400">/month</span>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <span className="text-orange-500 dark:text-orange-400 font-semibold">$29.99</span>
                      <span className="text-gray-600 dark:text-gray-400">/month</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="py-4 px-6 font-semibold text-gray-900 dark:text-white">Data</td>
                    <td className="py-4 px-6 text-center">
                      <button
                        type="button"
                        className="font-semibold cursor-pointer hover:opacity-80 text-[#49a078] focus:outline-none focus:ring-2 focus:ring-[#49a078] focus:ring-offset-2 rounded"
                        onClick={() => setShowPayGoDrawer(true)}
                      >
                        {getPayGoGBs()} in {homeCountryName}
                      </button>
                      <button
                        type="button"
                        className="text-xs text-gray-500 dark:text-gray-400 mt-1 cursor-pointer hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-[#49a078] focus:ring-offset-2 rounded block"
                        onClick={() => setShowPayGoBonusDrawer(true)}
                      >
                        + 5GB one-time bonus
                      </button>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <button
                        type="button"
                        className="font-semibold cursor-pointer hover:opacity-80 text-[#49a078] focus:outline-none focus:ring-2 focus:ring-[#49a078] focus:ring-offset-2 rounded"
                        onClick={() => setShowSoloDrawer(true)}
                      >
                        {calculateSoloGBs()} GB
                      </button>
                      <button
                        type="button"
                        className="text-xs text-gray-500 dark:text-gray-400 mt-1 cursor-pointer hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-[#49a078] focus:ring-offset-2 rounded block"
                        onClick={() => setShowSoloBonusDrawer(true)}
                      >
                        + 30GB bonus
                      </button>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <button
                        type="button"
                        className="font-semibold cursor-pointer hover:opacity-80 text-[#49a078] focus:outline-none focus:ring-2 focus:ring-[#49a078] focus:ring-offset-2 rounded"
                        onClick={() => setShowPremiumDrawer(true)}
                      >
                        {calculatePremiumGBs()} GB
                      </button>
                      <button
                        type="button"
                        className="text-xs text-gray-500 dark:text-gray-400 mt-1 cursor-pointer hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-[#49a078] focus:ring-offset-2 rounded block"
                        onClick={() => setShowPremiumBonusDrawer(true)}
                      >
                        + 120GB bonus
                      </button>
                    </td>
                  </tr>
                  <tr className="bg-gray-50 dark:bg-gray-700/50">
                    <td className="py-4 px-6 font-semibold text-gray-900 dark:text-white">Data Rollover</td>
                    <td className="py-4 px-6 text-center text-gray-700 dark:text-gray-300">Never expires</td>
                    <td className="py-4 px-6 text-center text-gray-700 dark:text-gray-300">3 months</td>
                    <td className="py-4 px-6 text-center text-gray-700 dark:text-gray-300">4 months</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-6 font-semibold text-gray-900 dark:text-white">Billing Style</td>
                    <td className="py-4 px-6 text-center text-gray-700 dark:text-gray-300">Pay as needed</td>
                    <td className="py-4 px-6 text-center text-gray-700 dark:text-gray-300">Monthly subscription</td>
                    <td className="py-4 px-6 text-center text-gray-700 dark:text-gray-300">Monthly subscription</td>
                  </tr>
                  <tr className="bg-gray-50 dark:bg-gray-700/50">
                    <td className="py-4 px-6 font-semibold text-gray-900 dark:text-white">eSIMs</td>
                    <td className="py-4 px-6 text-center text-gray-700 dark:text-gray-300">Up to 4</td>
                    <td className="py-4 px-6 text-center text-gray-700 dark:text-gray-300">Up to 2</td>
                    <td className="py-4 px-6 text-center text-gray-700 dark:text-gray-300">Up to 6</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-6 font-semibold text-gray-900 dark:text-white">Hotspot Sharing</td>
                    <td className="py-4 px-6 text-center text-[#49a078]">
                      <HiCheck className="w-5 h-5 mx-auto" />
                    </td>
                    <td className="py-4 px-6 text-center text-[#49a078]">
                      <HiCheck className="w-5 h-5 mx-auto" />
                    </td>
                    <td className="py-4 px-6 text-center text-[#49a078]">
                      <HiCheck className="w-5 h-5 mx-auto" />
                    </td>
                  </tr>
                  <tr className="bg-gray-50 dark:bg-gray-700/50">
                    <td className="py-4 px-6 font-semibold text-gray-900 dark:text-white">Ideal For</td>
                    <td className="py-4 px-6 text-center text-gray-700 dark:text-gray-300">Occasional travelers</td>
                    <td className="py-4 px-6 text-center text-gray-700 dark:text-gray-300">Individual users</td>
                    <td className="py-4 px-6 text-center text-gray-700 dark:text-gray-300">Families, multi-device users</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-6 font-semibold text-gray-900 dark:text-white">Flex Billing</td>
                    <td className="py-4 px-6 text-center text-orange-500 dark:text-orange-400">Not applicable</td>
                    <td className="py-4 px-6 text-center text-[#49a078]">Optional</td>
                    <td className="py-4 px-6 text-center text-[#49a078]">Optional</td>
                  </tr>
                  <tr className="bg-gray-50 dark:bg-gray-700/50">
                    <td className="py-4 px-6 font-semibold text-gray-900 dark:text-white">Data Speeds</td>
                    <td className="py-4 px-6 text-center text-gray-700 dark:text-gray-300">No Throttling</td>
                    <td className="py-4 px-6 text-center text-gray-700 dark:text-gray-300">No Throttling</td>
                    <td className="py-4 px-6 text-center text-gray-700 dark:text-gray-300">No Throttling</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-6"></td>
                    <td className="py-4 px-6 text-center">
                      <Link to="/newplan/paygo">
                        <button type="button" className="px-5 py-2 border-2 border-[#49a078] text-[#49a078] hover:bg-[#49a078] hover:text-white font-semibold rounded-lg transition-colors duration-200 text-sm">Get this plan</button>
                      </Link>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <Link to="/newplan/solo">
                        <button type="button" className="px-5 py-2 border-2 border-[#49a078] text-[#49a078] hover:bg-[#49a078] hover:text-white font-semibold rounded-lg transition-colors duration-200 text-sm">Get this plan</button>
                      </Link>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <Link to="/newplan/premium">
                        <button type="button" className="px-5 py-2 border-2 border-[#49a078] text-[#49a078] hover:bg-[#49a078] hover:text-white font-semibold rounded-lg transition-colors duration-200 text-sm">Get this plan</button>
                      </Link>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section - Chat-like Carousel */}
      <div id="faq" className="py-16 bg-accent transition-colors duration-200">
        <div className="container mx-auto px-4 md:px-8">
          <h2 className="text-3xl font-bold text-center mb-12 text-primary transition-colors duration-200">Frequently Asked Questions</h2>

          <div className="max-w-3xl mx-auto">
            {/* Chat-like FAQ Container */}
            <div
              className="relative min-h-[300px]"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              {faqData.map((faq, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-700 ${
                    currentFaqIndex === index ? 'opacity-100' : 'opacity-0 pointer-events-none'
                  }`}
                >
                  <div className="space-y-4">
                    {/* Question - Right aligned (user asking) */}
                    <div className="flex justify-end">
                      <div
                        onClick={() => handleFaqClick(index)}
                        onMouseEnter={() => {
                          setExpandedFaq(index);
                          setIsFaqHovered(true);
                        }}
                        onMouseLeave={() => {
                          setExpandedFaq(null);
                          setIsFaqHovered(false);
                        }}
                        className="max-w-[85%] bg-blue-500 text-white px-6 py-4 rounded-2xl rounded-tr-sm cursor-pointer hover:bg-blue-600 transition-colors duration-200 shadow-md"
                      >
                        <p className="text-lg font-medium">{faq.question}</p>
                      </div>
                    </div>

                    {/* Answer - Left aligned (jetogo answering) */}
                    <div
                      className={`flex justify-start transition-all duration-500 ${
                        expandedFaq === index ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
                      }`}
                    >
                      <div className="relative">
                        <div
                          className="max-w-[85%] bg-gray-200 dark:bg-gray-700 text-primary px-6 py-4 rounded-2xl rounded-tl-sm shadow-md cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
                          onMouseEnter={() => {
                            setExpandedFaq(index);
                            setIsFaqHovered(true);
                          }}
                          onMouseLeave={() => {
                            setExpandedFaq(null);
                            setIsFaqHovered(false);
                          }}
                          onClick={(e) => handleAnswerClick(e, index)}
                        >
                          <p className="text-base leading-relaxed">{faq.answer}</p>
                        </div>

                        {/* Selected Reaction Badge */}
                        {faqReactions[index] && (
                          <div className="absolute -bottom-2 -left-2 w-8 h-8 bg-white dark:bg-gray-800 rounded-full shadow-lg flex items-center justify-center border-2 border-gray-200 dark:border-gray-600">
                            <span className="text-lg">{faqReactions[index]}</span>
                          </div>
                        )}

                        {/* iOS-style Reaction Picker */}
                        {showReactionPicker === index && (
                          <div
                            className="absolute top-full left-0 mt-2 bg-white dark:bg-gray-800 rounded-full shadow-xl px-3 py-2 flex gap-2 border border-gray-200 dark:border-gray-700 z-10 animate-scale-in"
                            onMouseEnter={() => {
                              setExpandedFaq(index);
                              setIsFaqHovered(true);
                            }}
                            onMouseLeave={() => {
                              setExpandedFaq(null);
                              setIsFaqHovered(false);
                            }}
                          >
                            {['❤️', '👍', '👎', '❔'].map((emoji) => (
                              <button
                                key={emoji}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleReactionSelect(index, emoji);
                                }}
                                className="text-2xl hover:scale-125 transition-transform duration-150 active:scale-110"
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation Dots */}
            <div className="flex justify-center gap-2 mt-8">
              {faqData.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentFaqIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    currentFaqIndex === index
                      ? 'bg-gray-300 dark:bg-gray-500 w-8'
                      : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                  }`}
                  aria-label={`Go to FAQ ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div
        className="py-16 text-white relative"
        style={{
          backgroundImage: 'url(/assets/images/backgrounds/cta-background.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-black/40" />
        <div className="container mx-auto px-4 md:px-8 text-center relative z-10">
          <h2 className="text-3xl font-bold mb-4">Ready to Stay Connected Everywhere?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">Get a single eSIM that covers at home and on trips around the world.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button
              className="w-full sm:w-auto bg-white hover:bg-gray-100 text-gray-900 font-medium cursor-pointer"
              size="lg"
              onClick={handleFiveBucksSignup}
            >
              Start for $5. <HiArrowRight className="ml-2" />
            </Button>
          </div>
        </div>
      </div>
      
      {/* Data Calculator Drawers */}
      <React.Suspense fallback={null}>
        <RightDrawer
          isOpen={showPremiumDrawer}
          onClose={() => setShowPremiumDrawer(false)}
          title="Premium Plan Data Calculator"
        >
          <DataCalculator planType="premium" onHomeCountryChange={setHomeCountryPrice} />
        </RightDrawer>

        <RightDrawer
          isOpen={showSoloDrawer}
          onClose={() => setShowSoloDrawer(false)}
          title="Solo Plan Data Calculator"
        >
          <DataCalculator planType="solo" onHomeCountryChange={setHomeCountryPrice} />
        </RightDrawer>

        <RightDrawer
          isOpen={showPayGoDrawer}
          onClose={() => setShowPayGoDrawer(false)}
          title="Pay as You Go Data Calculator"
        >
          <DataCalculator planType="paygo" onHomeCountryChange={setHomeCountryPrice} />
        </RightDrawer>
      </React.Suspense>

      {/* Pending OAuth Modal */}
      <PendingOAuthModal
        show={showPendingOAuthModal}
        onClose={() => setShowPendingOAuthModal(false)}
        onGoogleAuth={() => {
          setShowPendingOAuthModal(false);
          authenticateWithGoogle();
        }}
        onAppleAuth={() => {
          setShowPendingOAuthModal(false);
          authenticateWithApple();
        }}
      />

      {/* Bonus Info Drawers */}
      <React.Suspense fallback={null}>
        <BonusInfoDrawer
          isOpen={showPremiumBonusDrawer}
          onClose={() => setShowPremiumBonusDrawer(false)}
          planType="premium"
        />

        <BonusInfoDrawer
          isOpen={showSoloBonusDrawer}
          onClose={() => setShowSoloBonusDrawer(false)}
          planType="solo"
        />

        <BonusInfoDrawer
          isOpen={showPayGoBonusDrawer}
          onClose={() => setShowPayGoBonusDrawer(false)}
          planType="paygo"
        />
      </React.Suspense>
    </div>
    </>
  );
};

export default HomePage;
